import { Button, FormField, Input, Modal, Select, Textarea } from "@closer/ui";
import { useEffect, useState } from "react";

/** Sunucunun `editableFields` anahtarları ↔ ekrandaki etiketler. */
const ETIKETLER: Record<string, string> = {
  title: "Başlık",
  contractType: "Tür",
  description: "Açıklama",
  referenceNumber: "Referans numarası",
  value: "Bedel",
  currency: "Para birimi",
  tags: "Etiketler",
};

export interface ContractBasics {
  title: string;
  contractType: string | null;
  description: string | null;
  referenceNumber: string | null;
  value: string | null;
  currency: string | null;
  tags: string[];
  version: number;
  editableFields: string[];
  types: { code: string; label: string }[];
}

/**
 * Temel bilgileri düzenleme (CLOS-110 `[072]`).
 *
 * ⚠ Hangi alanın çizileceğini SUNUCU söylüyor (`editableFields`). Alan matrisi
 * konfigürasyondan geliyor ve sözleşme TÜRÜNE bağlı: gizlilik sözleşmesinin parasal
 * değeri yok. Listeyi burada sabitlemek, kullanıcıya reddedilecek bir kutu doldurtmak
 * ve katalog değiştiğinde iki yeri birden güncellemek olurdu.
 *
 * ⚠ `version` her istekte taşınıyor: iki kullanıcı aynı sözleşmeyi aynı anda
 * düzenlediğinde ikincisi birincinin değişikliğini sessizce ezmesin.
 */
export function ContractBasicsDialog({
  open,
  onClose,
  mevcut,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  mevcut: ContractBasics;
  onConfirm: (girdi: Record<string, unknown>) => void;
  isPending: boolean;
}) {
  const [deger, setDeger] = useState<Record<string, string>>({});

  /**
   * Pencere AÇILDIĞINDA mevcut değerlerle kuruluyor; kapalıyken eski giriş kalmasın.
   *
   * ⚠ Bağımlılık YALNIZ `open` (kusur avı, 26 Ağustos). Eskiden `mevcut` de bağımlılıktaydı:
   * pencere açıkken sözleşme sorgusu arka planda tazelenince (sekmeye dönüşte yeniden çekme)
   * kullanıcının yazdığı taslak sunucu değerleriyle sessizce değiştiriliyordu. Kullanıcı
   * bunu fark etmeden Kaydet'e basıp hiç değiştirmediği eski metni geri gönderiyordu.
   * Eşzamanlılık kararı burada verilmiyor: `version` gönderim anında güncel `mevcut`tan
   * okunuyor ve çakışmayı sunucu bildiriyor.
   */
  useEffect(() => {
    if (!open) return;
    setDeger({
      title: mevcut.title,
      contractType: mevcut.contractType ?? "",
      description: mevcut.description ?? "",
      referenceNumber: mevcut.referenceNumber ?? "",
      value: mevcut.value ?? "",
      currency: mevcut.currency ?? "",
      tags: mevcut.tags.join(", "),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- taslak yalnız AÇILIŞTA beslenir
  }, [open]);

  const cizilecek = mevcut.editableFields.filter((k) => k in ETIKETLER);

  /**
   * ⚠ Alan listesi SUNUCUDAN geliyor ve sözleşmenin KAYITLI türüne göre hesaplanıyor —
   * yani kullanıcı buradaki türü değiştirdiğinde liste anında büyümüyor. Türü "Hizmet
   * sözleşmesi" yapıp Kaydet'e basan biri, Bedel alanını göremediği için ürünün o alanı
   * hiç istemediğini sanıyordu; alan ancak pencere İKİNCİ kez açıldığında beliriyor.
   *
   * Liste burada hesaplanamaz: alan↔tür matrisi sunucu konfigürasyonunda yaşıyor ve
   * yanıtta yalnız sonucu (`editableFields`) taşınıyor — matrisi ekranda kopyalamak,
   * katalog değiştiğinde iki yeri birden güncelleme borcu doğururdu. Bu yüzden ekran
   * uydurmuyor, olacağı söylüyor.
   */
  const turDegisti = (deger.contractType ?? "") !== (mevcut.contractType ?? "");

  function gonder() {
    const govde: Record<string, unknown> = { version: mevcut.version };
    for (const alan of cizilecek) {
      const ham = (deger[alan] ?? "").trim();
      if (alan === "tags") {
        govde.tags =
          ham.length > 0
            ? ham
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean)
            : [];
        continue;
      }
      /**
       * ⚠ Boş metin `null` gönderiliyor: şemada `null` "sildim", alanın hiç olmaması
       * "dokunmadım" demek. Boş metni olduğu gibi göndermek, temizlenmiş bir alanı boş
       * bir değere çevirirdi.
       */
      govde[alan] = ham.length > 0 ? ham : null;
    }
    // Başlık zorunlu: `null` gönderilemez, boşsa hiç gönderilmez.
    if (govde.title === null) delete govde.title;
    onConfirm(govde);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Temel bilgiler"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button onClick={gonder} loading={isPending}>
            Kaydet
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {cizilecek.map((alan) => (
          <FormField key={alan} label={ETIKETLER[alan]!} required={alan === "title"}>
            {alan === "contractType" ? (
              <Select
                aria-label={ETIKETLER[alan]}
                value={deger[alan] ?? ""}
                onChange={(e) => setDeger((d) => ({ ...d, [alan]: e.target.value }))}
              >
                <option value="">Seçilmedi</option>
                {mevcut.types.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.label}
                  </option>
                ))}
              </Select>
            ) : alan === "description" ? (
              <Textarea
                aria-label={ETIKETLER[alan]}
                rows={4}
                value={deger[alan] ?? ""}
                onChange={(e) => setDeger((d) => ({ ...d, [alan]: e.target.value }))}
              />
            ) : (
              <Input
                aria-label={ETIKETLER[alan]}
                value={deger[alan] ?? ""}
                onChange={(e) => setDeger((d) => ({ ...d, [alan]: e.target.value }))}
              />
            )}
          </FormField>
        ))}

        {turDegisti ? (
          <p className="text-sm text-fg-muted">
            Türe özgü alanlar (bedel gibi) seçilen türe göre belirlenir; tür değişikliğini
            kaydettikten sonra bu pencerede açılır.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
